import React, { useState, useReducer } from 'react';
import {
    calculatePaginationPages,
    calculateTotalPages,
    getDisplayedData,
} from '../selectors/converterSelector';

const INITIAL_STATE = {
    isLoading: {
        list: false,
        category: false,
    },
    userInputLoading: {},
    list: {},
    listDisplay: [],
    listParams: {
        filters: {},
        activePage: 1,
        perPage: 24,
        totalRecords: 1,
        sortBy: '',
        sortDirection: '',
    },
    userInput: {},
};

const listReducer = (state, action) => {
    switch (action.type) {
        case 'REPLACE_LIST':
            return action.list;

        case 'UPDATE_LIST':
            let newList = { ...state };

            Object.entries(action.list).map(([key, value]) => {
                newList[key] = {
                    ...newList[key],
                    ...value,
                };
            });

            return {
                ...state,
                ...newList,
            };

        default:
            throw new Error();
    }
};

/**
 * Helper function to get the initial state of the table correctly with the given config
 *
 * @param config
 * @returns {any}
 */
const getInitialState = (config) => {
    let initialState = { ...INITIAL_STATE };

    if (typeof config.isLoading != 'undefined') {
        initialState.isLoading = {
            ...initialState.isLoading,
            ...config.isLoading,
        };
    }

    if (typeof config.userInputLoading != 'undefined') {
        initialState.userInputLoading = {
            ...initialState.userInputLoading,
            ...config.userInputLoading,
        };
    }

    if (typeof config.list != 'undefined') {
        initialState.list = {
            ...initialState.list,
            ...config.list,
        };
    }

    if (typeof config.listDisplay != 'undefined') {
        initialState.listDisplay = {
            ...initialState.listDisplay,
            ...config.listDisplay,
        };
    }

    if (typeof config.listParams != 'undefined') {
        Object.entries(config.listParams).map(([listKey, listValue]) => {
            if (typeof listValue == 'object') {
                initialState.listParams = {
                    ...initialState.listParams,
                    [listKey]: {
                        ...initialState.listParams[listKey],
                        ...listValue,
                    },
                };
            } else if (Array.isArray(listValue)) {
                initialState.listParams = {
                    ...initialState.listParams,
                    [listKey]: [
                        ...initialState.listParams[listKey],
                        ...listValue,
                    ],
                };
            } else {
                initialState.listParams = {
                    ...initialState.listParams,
                    [listKey]: listValue,
                };
            }
        });
    }

    if (typeof config.userInput != 'undefined') {
        initialState.userInput = {
            ...initialState.userInput,
            ...config.userInput,
        };
    }

    return initialState;
};

function useManualReactTableState(config) {
    const initialState = getInitialState(config);

    const [isLoading, setLoading] = useState(initialState.isLoading);
    const [userInputLoading, setUserInputLoading] = useState(
        initialState.userInputLoading
    );
    const [list, dispatchList] = useReducer(listReducer, initialState.list);
    const [listDisplay, setListDisplay] = useState(initialState.listDisplay);
    const [listParams, setListParams] = useState(initialState.listParams);
    const [userInput, setUserInput] = useState(initialState.userInput);

    const getEffectWatcher = () => {
        let watchedParams = {
            ...listParams,
        };

        delete watchedParams.totalRecords;

        return JSON.stringify(watchedParams);
    };

    const getFilters = () => listParams.filters;
    const getTotalPages = () => calculateTotalPages(listParams);
    const getPaginationPages = () => calculatePaginationPages(listParams);
    const getDisplayedList = () => getDisplayedData(listDisplay, list);
    const getTotalRecords = () => listParams.totalRecords;
    const getActivePage = () => listParams.activePage;
    const getSort = () => {
        return {
            sortBy: listParams.sortBy,
            sortDirection: listParams.sortDirection,
        };
    };

    const isUserInputLoading = (identifier, key) => {
        if (
            typeof userInputLoading[key] != 'undefined' &&
            typeof userInputLoading[key][identifier] != 'undefined'
        ) {
            return userInputLoading[key][identifier];
        }

        return false;
    };

    const toggleLoading = (key, value) => {
        setLoading((prevState) => {
            return {
                ...prevState,
                [key]: value,
            };
        });
    };

    const toggleUserInputLoading = (identifier, key, value) => {
        setUserInputLoading((prevState) => {
            return {
                ...prevState,
                [key]: {
                    ...prevState[key],
                    [identifier]: value,
                },
            };
        });
    };

    const setActivePage = (page) => {
        setListParams((prevState) => {
            return {
                ...prevState,
                activePage: page,
            };
        });
    };

    const setTotalRecords = (value) => {
        setListParams((prevState) => {
            return {
                ...prevState,
                totalRecords: value,
            };
        });
    };

    const setSort = (key, direction) => {
        setListParams((prevState) => {
            return {
                ...prevState,
                sortBy: key,
                sortDirection: direction,
            };
        });
    };

    const setFilters = (filters) => {
        setListParams((prevState) => {
            return {
                ...prevState,
                filters: filters,
            };
        });
    };

    const updateUserInput = (identifier, key, value) => {
        setUserInput((prevState) => {
            return {
                ...prevState,
                [identifier]: {
                    ...prevState[identifier],
                    [key]: value,
                },
            };
        });
    };

    const clearUserInput = (identifier, key) => {
        let newUserInput = {
            ...userInput,
        };

        if (
            typeof newUserInput[identifier] != 'undefined' &&
            typeof newUserInput[identifier][key] != 'undefined'
        ) {
            delete newUserInput[identifier][key];
        }

        setUserInput(newUserInput);
    };

    const updateList = (newListData) => {
        dispatchList({
            type: 'UPDATE_LIST',
            list: newListData,
        });
    };

    /**
     * Handle react table onTableUpdate function
     *
     * @param tableState
     * @param tableInstance
     */
    const handleOnTableUpdate = (tableState, tableInstance) => {
        const { sortBy, sortDirection } = getSort();
        const { sorted } = tableState;

        if (sorted.length) {
            const newSortBy = sorted[0].id;
            const newSortDirection = sorted[0].desc ? 'DESC' : 'ASC';

            if (sortBy != newSortBy || sortDirection != newSortDirection) {
                setSort(newSortBy, newSortDirection);
            }
        } else {
            if (!(sortBy == '' && sortDirection == '')) {
                setSort(
                    initialState.listParams.sortBy,
                    initialState.listParams.sortDirection
                );
            }
        }
    };

    return {
        // Loading
        isLoading,
        toggleLoading,
        isUserInputLoading,
        userInputLoading,
        toggleUserInputLoading,

        // List
        list,
        listParams,
        listDisplay,
        getDisplayedList,
        setListDisplay,
        updateList,

        // User Input
        userInput,
        setUserInput,
        updateUserInput,
        clearUserInput,

        // Filter
        getFilters,
        setFilters,

        // Sort
        getSort,
        setSort,

        // Params
        getActivePage,
        getTotalPages,
        getPaginationPages,
        getTotalRecords,
        setActivePage,
        setTotalRecords,

        // Helper
        getEffectWatcher,
        handleOnTableUpdate,
    };
}

export default useManualReactTableState;
